import { Alert, AlertTitle, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState } from "react";
import { useRefresh } from "react-admin";
import { LaserReviewIcon } from "@/components/ui/mui/LaserReviewIcon";
import { HoldStrip } from "@/components/common/HoldStrip";
import { useDeductibleReviews } from "@/resources/claimants/hooks/useDeductibleReviews";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { reviewHelperText } from "@/resources/claimants/components/deductible-review/reviewHelperText";
import { DeductibleReviewDialog } from "@/resources/claimants/components/deductible-review/DeductibleReviewDialog";
import { useRecomputeRefreshOrStandalone } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { isAggregateSpecificPolicy } from "@/resources/clients/components/claims-summary/utils/aggregateSpecific";
import { useStoplossClaimContext } from "../hooks/useStoplossClaimContext";

/**
 * Claim-page hold banner (#7583). While a deductible review is open for the
 * claimant on this policy the API refuses submission with a 409
 * (`refuseSubmissionWhileReviewHeld`); this says so before the click and opens the
 * review dialog in place, so the claim page stays underneath. Text comes from the same builder as the claimant banner and flag
 * tooltip, so the three never disagree. Returns null when no review is open.
 *
 * Not covered: an ASD claim held by another claimant's review on the policy;
 * the API still holds it, this banner cannot see it.
 */
/** `compact`: stacked layout for the action rail (title, text, full-width button). */
export const StoplossClaimHoldBanner = ({ compact = false }: { compact?: boolean }) => {
  const { claim, displayClaimantId, claimant, policy, reviewPending } = useStoplossClaimContext();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  // The dialog unmounts on close, so the deferred recompute refresh is owned here.
  const { triggerRecomputeRefresh } = useRecomputeRefreshOrStandalone();
  const onResolved = () => {
    triggerRecomputeRefresh({ notifyUser: false });
    refresh();
  };
  const claimantId = displayClaimantId;
  const policyId = claim.policy_id_ref ? String(claim.policy_id_ref) : undefined;
  const { openReview } = useDeductibleReviews(claimantId, policyId, reviewPending);
  const { conditions } = useLaseredConditions(policyId, claimantId, reviewPending);

  if (!reviewPending || !claimantId || !policyId) return null;

  const detail = reviewHelperText(openReview, conditions);
  // Resolve in place (#7583): the dialog is self-contained, so the analyst never
  // leaves the claim. The claimant page's deep link still works for tasks.
  const dialog = open ? (
    <DeductibleReviewDialog
      open={open}
      onClose={() => setOpen(false)}
      onResolved={onResolved}
      claimantId={claimantId}
      policyId={policyId}
      claimantName={claimant?.claimant_name}
      asdPolicy={isAggregateSpecificPolicy(policy)}
    />
  ) : null;

  if (compact) {
    // In-card strip: sits in the claim card where the two action buttons were, so
    // the blocker takes the place of the actions it blocks (shared HoldStrip).
    const reason = detail.replace(/\s*Click to review\.?$/, "");
    return (
      <>
        <HoldStrip
          testId="claim-hold-banner"
          title="On hold: deductible review open"
          reason={reason}
          action={{ label: "Open the review", onClick: () => setOpen(true) }}
        />
        {dialog}
      </>
    );
  }

  return (
    <Alert
      severity="warning"
      role="status"
      data-testid="claim-hold-banner"
      icon={<LaserReviewIcon size={22} />}
      sx={{
        alignItems: "center",
        "& .MuiAlert-message": { fontSize: "0.8125rem", width: "100%" },
        "& .MuiAlert-action": { pt: 0, alignItems: "center" },
      }}
      action={
        <Button
          variant="contained"
          color="warning"
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={() => setOpen(true)}
          disableElevation
        >
          Open the review
        </Button>
      }
    >
      <AlertTitle sx={{ fontSize: "0.875rem", mb: 0.25 }}>
        On hold: a deductible review is open for this claimant.
      </AlertTitle>
      {detail} Funding requests and carrier submission wait until it closes.
      {dialog}
    </Alert>
  );
};
